import {
  InstagramIcon,
  LinkedinIcon,
  TwitterIcon,
  YoutubeIcon,
  TikTokIcon,
} from "lucide-react";
import Button from "../UI/Button";
import footerLogo from "../../assets/footerLogo.png";
import Logo from "../../assets/Autograph-logo-01 1.svg";
import footerStory from "../../assets/footerStory.png";
import micEmoji from "../../assets/mic-3d_90deg.png";
import { useScrollAnimation } from "../../hooks/useScrollAnimation";
import { Link } from "react-router-dom";

const Footer = () => {
  const { ref: heroRef, isVisible: heroVisible } = useScrollAnimation({ threshold: 0.2 });
  const { ref: linksRef, isVisible: linksVisible } = useScrollAnimation({ threshold: 0.2 });
  const { ref: socialRef, isVisible: socialVisible } = useScrollAnimation({ threshold: 0.5 });

  return (
    <footer className="bg-black text-white py-6 sm:py-8 md:py-10 lg:py-12">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-5 lg:px-6 xl:px-8">
        <div className="flex flex-col">
          {/* Hero section with animations */}
          <div ref={heroRef} className="flex flex-col space-y-4 sm:space-y-5 md:space-y-6 lg:space-y-7 xl:space-y-8 text-center">
            <img 
              src={micEmoji} 
              alt="Mic emoji" 
              className={`mx-auto w-32 h-32 sm:w-40 sm:h-40 md:w-48 md:h-48 lg:w-56 lg:h-56 xl:w-60 xl:h-60 2xl:w-64 2xl:h-64 my-[-20px] sm:my-[-30px] md:my-[-40px] lg:my-[-50px] xl:my-[-58px] transition-all duration-700 ${
                heroVisible ? "opacity-100 scale-100" : "opacity-0 scale-75"
              }`}
              style={{ animation: heroVisible ? 'floatGentle 5s ease-in-out infinite' : 'none' }}
            />
            <p className={`font-sourceSans font-black tracking-tighter text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl 2xl:text-7xl text-white transition-all duration-700 ${
              heroVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`} style={{ transitionDelay: '100ms' }}>
              Start making <span className="font-light italic">fun</span>
              <span className="block font-light italic">content</span>
            </p>

            <p className={`text-ag-black-300 font-medium text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl px-4 transition-all duration-700 ${
              heroVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`} style={{ transitionDelay: '200ms' }}>
              Browse and remix template components
              <span className="block">without hassle.</span>
            </p>
            <div className={`transition-all duration-700 ${
              heroVisible ? "opacity-100 translate-y-0 scale-100" : "opacity-0 translate-y-8 scale-90"
            }`} style={{ transitionDelay: '300ms' }}>
              <Link to="/templates">
                <Button
                  variant="outline"
                  size="md"
                  className="bg-ag-green hover:bg-inherit border border-ag-green text-base sm:text-lg md:text-xl lg:text-2xl xl:text-3xl font-medium text-black px-6 sm:px-8 md:px-10 lg:px-12 hover:scale-105 hover:shadow-lg hover:shadow-ag-green/30 transition-all duration-300 btn-press ripple-effect"
                >
                  Try now
                </Button>
              </Link>
              
            </div>
          </div>
          
          {/* Links section with staggered animations */}
          <div 
            ref={linksRef}
            className="flex flex-col gap-6 sm:gap-4 md:gap-0 mx-auto md:mx-0 md:flex-row md:justify-between mt-8 sm:mt-10 md:mt-12 text-center md:text-left"
          >
            <div className={`flex flex-col gap-1 transition-all duration-700 ${
              linksVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`} style={{ transitionDelay: '0ms' }}>
              <p className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold text-ag-black-400 mb-2 sm:mb-3 md:mb-4">PRODUCT</p>
              <p className="font-medium text-sm sm:text-base md:text-lg lg:text-xl hover:text-ag-green transition-colors duration-300 cursor-pointer underline-animate">Template editor</p>
              <p className="font-medium text-sm sm:text-base md:text-lg lg:text-xl hover:text-ag-green transition-colors duration-300 cursor-pointer underline-animate">Remix component</p>
              <p className="font-medium text-sm sm:text-base md:text-lg lg:text-xl hover:text-ag-green transition-colors duration-300 cursor-pointer underline-animate">Browse library</p>
              <p className="font-medium text-sm sm:text-base md:text-lg lg:text-xl hover:text-ag-green transition-colors duration-300 cursor-pointer underline-animate">Dynamic properties</p>
            </div>
            <div className={`flex flex-col gap-1 transition-all duration-700 ${
              linksVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`} style={{ transitionDelay: '100ms' }}>
              <p className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold text-ag-black-400 mb-2 sm:mb-3 md:mb-4">COMPANY</p>
              <p className="font-medium text-sm sm:text-base md:text-lg lg:text-xl hover:text-ag-green transition-colors duration-300 cursor-pointer underline-animate">About</p>
              <p className="font-medium text-sm sm:text-base md:text-lg lg:text-xl hover:text-ag-green transition-colors duration-300 cursor-pointer underline-animate">News/Update</p>
              <p className="font-medium text-sm sm:text-base md:text-lg lg:text-xl hover:text-ag-green transition-colors duration-300 cursor-pointer underline-animate">Beta version</p>
            </div>
            <div className={`flex flex-col gap-1 transition-all duration-700 ${
              linksVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`} style={{ transitionDelay: '200ms' }}>
              <p className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold text-ag-black-400 mb-2 sm:mb-3 md:mb-4">
                GET STARTED
              </p>
              <p className="font-medium text-sm sm:text-base md:text-lg lg:text-xl hover:text-ag-green transition-colors duration-300 cursor-pointer underline-animate">Creatives</p>
              <p className="font-medium text-sm sm:text-base md:text-lg lg:text-xl hover:text-ag-green transition-colors duration-300 cursor-pointer underline-animate">Log in/Sign up</p>
              <Link to="/pricing" className="font-medium text-sm sm:text-base md:text-lg lg:text-xl hover:text-ag-green transition-colors duration-300 cursor-pointer underline-animate">Pricing</Link>
            </div>
          </div>
          
          {/* Social section with animations */}
          <div ref={socialRef} className="flex flex-col mt-6 sm:mt-8 md:mt-10">
            <div className={`flex flex-col md:flex-row mt-4 sm:mt-6 md:mt-8 w-full items-center justify-center gap-4 md:gap-0 md:space-x-6 transition-all duration-700 ${
              socialVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}>
              <img src={Logo} alt="Logo" className="h-6 sm:h-8 md:h-10 w-auto hover:scale-110 transition-transform duration-300" />

              <p className="text-ag-black-400 font-medium text-xs sm:text-sm md:text-base lg:text-lg xl:text-xl text-center">
                &copy; {new Date().getFullYear()} AUTOGRAPH RESERVED / TERMS OF
                USE /<span className="block sm:inline"> <Link to="/privacy" className="hover:text-ag-green transition-colors duration-300 underline-animate">PRIVACY POLICY</Link></span>
              </p>
            </div>
            <div className={`flex w-full max-w-xs sm:max-w-sm md:max-w-md items-center justify-between mx-auto mt-10 sm:mt-14 md:mt-16 lg:mt-18 xl:mt-20 transition-all duration-700 ${
              socialVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`} style={{ transitionDelay: '200ms' }}>
              <a href="https://www.youtube.com/@Autograph_Video" target="_blank" rel="noopener noreferrer" className="hover:text-ag-green transition-all duration-300 hover:scale-125 hover:-translate-y-1">
                <YoutubeIcon className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7" />
              </a>
              <a href="https://www.instagram.com/autograph.video?utm_source=ig_web_button_share_sheet&igsh=ZDNlZDc0MzIxNw==" target="_blank" rel="noopener noreferrer" className="hover:text-ag-green transition-all duration-300 hover:scale-125 hover:-translate-y-1">
                <InstagramIcon className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7" />
              </a>
              <a href="/" target="_blank" rel="noopener noreferrer" className="hover:text-ag-green transition-all duration-300 hover:scale-125 hover:-translate-y-1">
                <TwitterIcon className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7" />
              </a>
            </div>
           
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
